/**
 * CORE-1356 — ужать картинку перед отправкой в чат с Агапеллой.
 *
 * Картинка уходит в модель не описанием, а собой: входные токены считаются за
 * неё так же, как за текст. Замер на gpt-5.4-mini: фото с телефона 1290×2796
 * — 2304 токена, снимок камеры 3024×4032 — 2941, а та же картинка со стороной
 * 512 — 307. Бюджет всего хода — 9000 токенов, то есть неужатое фото занимает
 * четверть разговора и стоит примерно в семь раз дороже ужатого.
 *
 * Сторона 1024 — не самый дешёвый вариант, а тот, после которого дешевле уже
 * не имеет смысла: рукописную страницу плана питания на 1024 ещё видно, на 512
 * уже нет, а ради этого всё и делается.
 *
 * Правки не деструктивные для человека: у него на устройстве остаётся
 * оригинал, ужимается только то, что уходит на сервер.
 *
 * Web-only — здесь есть canvas. На нативе функция возвращает исходный файл, и
 * это осознанно: нативный композер в ИИ-треде живёт своей дорогой (sendMedia),
 * и молча менять его поведение отсюда было бы хуже, чем не трогать.
 */

/** Длинная сторона, до которой ужимаем. */
export const MAX_IMAGE_SIDE = 1024;

/** Ниже этого размера ужимать нечего — накладные расходы дороже выгоды. */
export const DOWNSCALE_MIN_BYTES = 200 * 1024;

/** Проверяется в момент вызова, а не при импорте: на нативе модуль всё равно
 *  загружается, и замер окружения на старте пришлось бы держать в голове. */
const hasCanvas = () =>
  typeof document !== 'undefined' && typeof HTMLCanvasElement !== 'undefined';

function canDownscale(file: File | Blob): boolean {
  if (!hasCanvas()) return false;
  const type = (file as any)?.type || '';
  // GIF теряет анимацию при перерисовке в canvas — оставляем как есть.
  if (!type.startsWith('image/') || type === 'image/gif') return false;
  const size = (file as any)?.size ?? 0;
  return size >= DOWNSCALE_MIN_BYTES;
}

async function loadBitmap(file: File | Blob): Promise<{ width: number; height: number; draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void } | null> {
  // createImageBitmap — быстрый путь и он есть везде, где есть canvas; <img>
  // оставлен запасным, чтобы отсутствие одного API не отменяло всю правку.
  try {
    if (typeof createImageBitmap === 'function') {
      const bitmap = await createImageBitmap(file as any);
      return {
        width: bitmap.width,
        height: bitmap.height,
        draw: (ctx, w, h) => ctx.drawImage(bitmap as any, 0, 0, w, h),
      };
    }
  } catch {
    // Падение расшифровки — не повод ронять отправку; идём ниже.
  }
  try {
    const url = URL.createObjectURL(file);
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });
    URL.revokeObjectURL(url);
    return {
      width: img.naturalWidth,
      height: img.naturalHeight,
      draw: (ctx, w, h) => ctx.drawImage(img, 0, 0, w, h),
    };
  } catch {
    return null;
  }
}

/**
 * Вернуть ужатую копию картинки — или исходный файл, если ужимать нечего или
 * не получилось. Никогда не бросает: не смогли ужать — отправляем как есть,
 * потому что дороже отправки только неотправленное сообщение.
 */
export async function downscaleImage(
  file: File | Blob,
  maxSide: number = MAX_IMAGE_SIDE,
): Promise<File | Blob> {
  if (!canDownscale(file)) return file;

  const source = await loadBitmap(file);
  if (!source || !source.width || !source.height) return file;

  const longest = Math.max(source.width, source.height);
  if (longest <= maxSide) return file;

  const scale = maxSide / longest;
  const width = Math.max(1, Math.round(source.width * scale));
  const height = Math.max(1, Math.round(source.height * scale));

  try {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    source.draw(ctx, width, height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.85);
    });
    if (!blob || blob.size >= ((file as any)?.size ?? Infinity)) return file;

    const name = ((file as any)?.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg';
    // File, а не Blob: дальше по дороге имя читают и валидация, и бэкенд.
    if (typeof File === 'function') {
      return new File([blob], name, { type: 'image/jpeg' });
    }
    (blob as any).name = name;
    return blob;
  } catch {
    return file;
  }
}
